<template>
  <div class="bragg-pro-fixed">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$2d · sin(θ) = nλ$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>入射角 θ</label>
            <span class="val">{{ theta.toFixed(1) }}°</span>
          </div>
          <div class="hud-item orange">
            <label>晶面间距 d</label>
            <span class="val">{{ d.toFixed(2) }} <small>Å</small></span>
          </div>
          <div class="hud-item" :class="isBraggMatched ? 'green' : 'gray'">
             <label>干涉增强状态</label>
             <span class="val">{{ isBraggMatched ? '发生衍射峰' : '无明显反射' }}</span>
          </div>
        </div>

        <svg width="100%" height="240" viewBox="0 0 450 240" preserveAspectRatio="xMidYMid meet">
          <!-- 晶体点阵 (Crystal Lattice) -->
          <g v-for="layer in 4" :key="'l'+layer" :transform="`translate(0, ${80 + layer*d*30})`">
             <circle v-for="i in 10" :key="'a'+i" :cx="i*40+25" cy="0" r="4" fill="#64748b" opacity="0.6" />
             <line x1="0" y1="0" x2="450" y2="0" stroke="#334155" stroke-dasharray="2,2" />
          </g>

          <!-- 入射光线群 (Incident Rays) -->
          <line v-for="layer in 3" :key="'i'+layer" 
                :x1="225 - 150*Math.cos(rad)" :y1="110 + layer*d*30 - 150*Math.sin(rad)" 
                x2="225" :y2="110 + layer*d*30" 
                stroke="#fbbf24" :opacity="isBraggMatched ? 1 : 0.4" stroke-width="2" />
          
          <!-- 反射光线群 (Diffracted Rays) -->
          <line v-for="layer in 3" :key="'r'+layer" 
                x2="225 + 150*Math.cos(rad)" :y2="110 + layer*d*30 - 150*Math.sin(rad)" 
                x1="225" :y1="110 + layer*d*30" 
                stroke="#fbbf24" :opacity="isBraggMatched ? 1 : 0.1" stroke-width="3" filter="url(#glow-b)" />
          
          <defs>
             <filter id="glow-b"><feGaussianBlur stdDeviation="3" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
          </defs>
        </svg>

        <div class="physics-caption">
           规律：只有当光在相邻晶面的光程差恰好为波长的整数倍时，反射光才会彼此相长增强。
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex-row items-center gap-6">
              <div class="flex-1">
                 <label class="s-label">调节波入射角度 (θ)：{{ theta.toFixed(1) }}°</label>
                 <el-slider v-model="theta" :min="5" :max="85" size="small"></el-slider>
              </div>
              <div class="flex-1">
                 <label class="s-label">调节晶面间距 d (单位：埃级)：{{ d }}</label>
                 <el-slider v-model="d" :min="0.5" :max="2.5" :step="0.05" size="small"></el-slider>
              </div>
           </div>
        </div>
        
        <!-- 高对比度蓝色专业卡片加固 -->
        <div class="discovery-card-discovery-tir" v-latex>
           <div class="icon-wrap"><i class="el-icon-aim"></i></div>
           <div class="text-content">
             <p><strong>实战物理/材料科学：</strong>布拉格定律是由于相邻原子层反射的光发生**干涉**产生的。<strong>解惑：</strong>肉眼看不见的晶体结构，由于其规律性，变成了光速最好的“筛子”。<strong>观察：</strong>调节角度，你会发现只有在特定位置反射光猛然变亮。这正是我们测定 DNA 结构、研发新型半导体材料的核心手段。
             </p>
           </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'BraggDiffractionProViz',
  data() {
    return {
      theta: 30,
      d: 1.2,
      lambda: 1.2 // X-ray wavelength roughly
    }
  },
  computed: {
    rad() { return this.theta * Math.PI / 180; },
    isBraggMatched() {
       // Check 2d sin t = n lambda
       const nLambda = 2 * this.d * Math.sin(this.rad);
       return Math.abs(nLambda - Math.round(nLambda / this.lambda) * this.lambda) < 0.15;
    }
  }
}
</script>

<style scoped lang="scss">
@import "./PhysicsCommon.scss";
.bragg-pro-fixed { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
</style>
